import React, { useEffect, useState } from "react"
import Layout from "../../components/layout"
import SEO from "../../components/seo"
import { graphql, Link } from "gatsby"
import nl2br from "../../utils/nl2br"
import { Map, Marker } from "../../components/Map"
import RetinaImageWrapper from "../../components/RetinaImage"

import "./styles.less"

export default ({ data }) => {
  const meetingRooms = data.cms.meetingRooms
  const locations = data.cms.locations
  const pageData = data.cms.pageData

  locations.map((location) => {
    location.rooms = meetingRooms.filter(
      (mr) => mr?.mrLocation?.[0]?.id === location.id
    )
    return null
  })

  const globals = data.cms.globals

  return (
    <Layout mainClassName={"meeting-rooms-container"}>
      <SEO
        title={pageData?.metaTitle}
        description={pageData?.metaDescription}
        image={
          pageData?.metaImage[0]
            ? pageData?.metaImage[0].url
            : globals?.metaImage[0].url
        }
        jsonSchema={pageData?.jsonSchema}
      />

      <div className="container">
        <div className="row">
          <div className="col-lg-6">
            <h1>{nl2br(pageData.pageTitle)}</h1>
            <p className={"mb-0"}>{nl2br(pageData.pageSubtitle)}</p>
          </div>
        </div>
      </div>

      {locations.map((location) => {
        if (location.rooms.length <= 0) {
          return null
        }
        return <MeetingRoomSection key={location.id} location={location} />
      })}
    </Layout>
  )
}

const MeetingRoomSection = ({ location }) => {
  const [height, setHeight] = useState(0)

  useEffect(() => {
    const mr = document.getElementsByClassName("meeting-room")

    if (mr.length > 0 && typeof window !== "undefined") {
      if (window.innerWidth < 768) {
        setHeight(0)
      } else {
        setHeight(mr[0].clientHeight)
      }
    }
  }, [])

  return (
    <div className="container">
      <div
        className="meeting-room-wrapper"
        id={location.slug}
        name={location.slug}
      >
        <div className="row">
          <div className="col-12">
            <div className="row">
              <div className="col-md-6">
                <h3 className={"mt-0"}>{location.title}</h3>
                <p className="subtitle d-md-none">{location.shortAddress}</p>
              </div>
              <div className="col-md-6 text-end d-flex align-items-center justify-content-end">
                <Link
                  to={`/locations/${location.slug}`}
                  className={"chev-right btn btn-link"}
                >
                  Explore location
                </Link>
                {process.env.GATSBY_TEMPORARY_NOT_AVAILABLE ? (
                  <Link
                    to={`/${process.env.GATSBY_TEMPORARY_NOT_AVAILABLE}`}
                    className="btn btn-primary ms-auto ms-md-0"
                    target={"_blank"}
                    rel="noopener noreferrer"
                  >
                    Book Now
                  </Link>
                ) : (
                  <a
                    href={location.bookNowLink}
                    className="btn btn-primary ms-auto ms-md-0"
                    target={"_blank"}
                    rel="noopener noreferrer"
                  >
                    Book Now
                  </a>
                )}
              </div>
              <div className="col-12 d-none d-md-block">
                <p className="subtitle">{location.shortAddress}</p>
              </div>
            </div>
          </div>

          <div className="col-md-4 order-md-2">
            <div className="meeting-room-map" style={{ height: `${height}px` }}>
              <Map
                center={{
                  lat: location.location.lat,
                  lng: location.location.lng,
                }}
                defaultZoom={13}
              >
                <Marker
                  slug={`/locations/${location.slug}`}
                  id={location.id}
                  text={location.title}
                  lat={location.location.lat}
                  lng={location.location.lng}
                />
              </Map>
            </div>
          </div>

          <div className="col-md-8 order-md-1">
            <div className="row">
              {location.rooms.map((room, idx) => (
                <div className="col-sm-6" key={room.id}>
                  <div
                    className={`meeting-room ${idx < 2 ? "remove-margin" : ""}`}
                  >
                    <div className="mr-image">
                      <RetinaImageWrapper
                        image={room.mrImage[0]}
                        retinaImage={room.mrImageRetina[0]}
                        className={"main-image"}
                      />
                    </div>
                    <h4>{room.mrTitle}</h4>
                    <div className="row">
                      <div className="col-6">
                        <p>Hourly</p>
                        <p className={"price"}>
                          <strong>${room.hourlyRate}/Hour</strong>
                        </p>
                      </div>
                      <div className="col-6">
                        <p>Daily</p>
                        <p className={"price"}>
                          <strong>${room.dailyRate}/Day</strong>
                        </p>
                      </div>
                      <div className="col-sm-12">
                        <ul className="room-amenities">
                          {room.amenities.split("\n").map((text, idx) => (
                            <li key={idx}>{text}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export const query = graphql`
  query {
    cms {
      pageData: entry(section: "meetingRoomsPage") {
        id
        ... on Cms_meetingRoomsPage_meetingRooms_Entry {
          id
          pageTitle
          metaTitle
          pageSubtitle
          metaDescription
          jsonSchema
          metaImage {
            url(width: 1200, height: 630)
          }
        }
      }
      locations: entries(section: "locations") {
        id
        title
        ... on Cms_locations_locations_Entry {
          id
          slug
          shortAddress
          bookNowLink
          location {
            address
            lat
            lng
            parts {
              city
              address
            }
          }
        }
      }
      globals: globalSet {
        id
        ... on Cms_metadata_GlobalSet {
          id
          metaImage {
            url(width: 1200)
          }
        }
      }
      meetingRooms: entries(section: "meetingRooms") {
        id
        ... on Cms_meetingRooms_meetingRooms_Entry {
          id
          hourlyRate
          dailyRate
          mrTitle: title
          mrImage {
            url(width: 400, height: 280)
            title
          }
          mrImageRetina: mrImage {
            url(width: 800, height: 560)
            title
          }
          amenities
          mrLocation {
            id
            slug
            ... on Cms_locations_locations_Entry {
              id
              title
              shortAddress
              location {
                lat
                lng
                parts {
                  address
                }
              }
            }
          }
        }
      }
    }
  }
`
